import { useEffect, useRef, useState } from 'react'
import { Users } from 'lucide-react'
import { ErpTable, Panel, Td, Th } from '@/components/erp/primitives'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Sparkbars } from '@/shared/ui/charts'
import { fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { MobilizerCompareResponse } from '@/shared/api/types'
import { compareMobilizers, listMobilizers } from './api'
import { TierChip, roleLabel, scoreNumber } from './vocab'

/**
 * Side-by-side, two to five people.
 *
 * ## Why the bound is two to five
 *
 * One is the detail drawer. Six is a table, and a table sorted by score is a
 * better answer than six columns nobody can read across. The server refuses
 * anything outside the range naming both bounds, and this panel does not offer
 * a sixth chip rather than letting somebody discover the limit as an error.
 *
 * ## Rows keep their position when somebody has no data
 *
 * A mobilizer with no rollup row comes back with nulls in the request's order,
 * not dropped. Dropping them would silently shift every column after them — the
 * fourth person's numbers would appear under the third person's name, which is
 * the kind of bug nobody reports because it looks like data.
 *
 * ## The best cell is marked, and ties are marked together
 *
 * PollSphere crowned `values.indexOf(max)`, so a tie silently crowned whoever
 * happened to be leftmost. Here every cell holding the maximum is marked, and a
 * row where everybody is zero marks nobody — nought contacts each is not a
 * three-way tie for first, it is nothing happening.
 */

const METRICS: { key: keyof MetricRow; label: string }[] = [
  { key: 'score', label: 'Score' },
  { key: 'activitiesTotal', label: 'Activities' },
  { key: 'votersRegistered', label: 'Voters registered' },
  { key: 'votersVerified', label: 'Voters verified' },
  { key: 'votersContacted', label: 'Voters contacted' },
  { key: 'transfersRaised', label: 'Transfers raised' },
  { key: 'transfersRecorded', label: 'Transfers recorded' },
  { key: 'tasksCompleted', label: 'Tasks finished' },
  { key: 'tasksExpired', label: 'Tasks expired' },
  { key: 'streakDays', label: 'Streak (days)' },
]

interface MetricRow {
  score: number | null
  activitiesTotal: number
  votersRegistered: number
  votersVerified: number
  votersContacted: number
  transfersRaised: number
  transfersRecorded: number
  tasksCompleted: number
  tasksExpired: number
  streakDays: number
}

/** Expired tasks are the one metric where more is worse, so the mark follows the minimum. */
const LOWER_IS_BETTER = new Set<keyof MetricRow>(['tasksExpired'])

export function ComparePanel({ from, to }: { from: string; to: string }) {
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<MobilizerCompareResponse | null>(null)

  const run = async (next: { value: string; label: string }[]) => {
    setPicked(next)
    if (next.length < 2) { setResult(null); return }
    setBusy(true); setError(null)
    try {
      setResult(await compareMobilizers(next.map((p) => p.value), from, to))
    } catch (err) {
      setError(explain(err))
      setResult(null)
    } finally { setBusy(false) }
  }

  // The heading says "over the range above", so the range moving has to move the
  // numbers. Without this the panel keeps a 7-day comparison under a 90-day
  // label for as long as the user leaves it open.
  const runRef = useRef(run)
  runRef.current = run
  const pickedKey = picked.map((p) => p.value).join(',')
  useEffect(() => {
    if (picked.length < 2) return
    void runRef.current(picked)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, pickedKey])

  const series = result?.series ?? []

  return (
    <Panel
      title="Compare"
      description="Two to five people, side by side, over the range above."
      right={picked.length > 0
        ? <span className="text-erp-xs text-muted-foreground">{picked.length} of 5</span>
        : undefined}
    >
      <div className="space-y-3">
        <AsyncMultiSelect
          label="Mobilizers"
          selected={picked}
          // Cap at five here rather than letting the server refuse: the limit is a
          // property of the comparison, and discovering it as a 400 is worse.
          onChange={(next) => void run(next.slice(0, 5))}
          placeholder="Search by name"
          search={async (term) => {
            const page = await listMobilizers({ q: term }, undefined, 25)
            return page.content.map((m) => ({
              value: m.id, label: m.fullName,
              sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
            }))
          }}
        />

        <ErrorStrip message={error} />

        {picked.length < 2 ? (
          <EmptyState
            title="Pick at least two"
            hint="One person is the detail drawer; six is a sorted table. Two to five is where a side-by-side reads."
          />
        ) : busy && !result ? (
          <LoadingBlock label="Comparing…" />
        ) : series.length === 0 ? null : (
          <>
            <ErpTable head={<>
              <Th>Metric</Th>
              {series.map((s) => (
                <Th key={s.mobilizerId} className="text-right">
                  <span className="block truncate max-w-36">{s.fullName}</span>
                  <span className="block font-normal normal-case text-erp-xs text-muted-foreground truncate">
                    {roleLabel(s.roleCode)}
                  </span>
                </Th>
              ))}
            </>}>
              <tr>
                <Td>Tier</Td>
                {series.map((s) => (
                  <Td key={s.mobilizerId} className="text-right">
                    <TierChip code={s.tier} />
                  </Td>
                ))}
              </tr>
              {METRICS.map((m) => {
                const values = series.map((s) => (
                  m.key === 'score' ? scoreNumber(s.score) : (s[m.key] as number)
                ))
                const numeric = values.filter((v): v is number => v !== null)
                const best = numeric.length === 0 ? null
                  : LOWER_IS_BETTER.has(m.key) ? Math.min(...numeric) : Math.max(...numeric)
                // Nought each is not a three-way tie for first.
                const markable = best !== null && best !== 0
                return (
                  <tr key={m.key} className="hover:bg-muted/40">
                    <Td>{m.label}</Td>
                    {values.map((v, i) => (
                      <Td
                        key={series[i]!.mobilizerId}
                        className={markable && v === best
                          ? 'text-right erp-metric font-semibold text-chocolate'
                          : 'text-right erp-metric'}
                        title={markable && v === best
                          ? (LOWER_IS_BETTER.has(m.key) ? 'Lowest of those compared' : 'Highest of those compared')
                          : undefined}
                      >
                        {v === null ? '—' : fmtNumber(v)}
                      </Td>
                    ))}
                  </tr>
                )
              })}
            </ErpTable>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {series.map((s) => (
                <div key={s.mobilizerId} className="erp-card px-3 py-2 min-w-0">
                  <div className="flex items-center gap-1.5 text-erp-sm font-medium truncate">
                    <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    {s.fullName}
                  </div>
                  <Sparkbars
                    points={s.daily.map((p) => ({ label: p.day.slice(5), value: p.count }))}
                    height={52}
                    emptyMessage="Nothing in this range."
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}
